import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type {
  AiVerificationAssignmentPlan,
  AiVerificationAssignmentPlanRun,
  AiVerificationGenerationConstraints,
  AiVerificationPlanStrategy,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * 配分の予約 (`districts/{districtId}/aiVerificationAssignmentPlans/{planId}`) の Firestore hook / helper
 * (2026-09-04、REQ-4.3.20)。
 *
 * 予約 = イベント前に保存する「配り方」。実行 (投稿・参加者の自動収集 → 配分セット生成) は callable
 * `runAssignmentPlan` (`useRunAssignmentPlan`) が行い、本ファイルは予約 doc の購読と CRUD のみ。
 * 認可は firestore.rules (`isDistrictAdmin`) が担うため、`useAssignmentSetEditor` と同じく
 * クライアント直 setDoc で書く。undefined キーは Firestore が拒否するため payload から除外する。
 */

export type AssignmentPlansState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: AiVerificationAssignmentPlan[]; error: null }
  | { status: 'error'; items: []; error: Error };

/** 予約フォームが扱う入力 (監査系・runs・runLock は含まない)。 */
export interface AssignmentPlanInput {
  displayName: string;
  description: string;
  templateId: string;
  scopeMode: AiVerificationAssignmentPlan['scopeMode'];
  targetSessionIds: string[];
  strategy: AiVerificationPlanStrategy;
  constraints: AiVerificationGenerationConstraints;
  candidateFilters: AiVerificationAssignmentPlan['candidateFilters'];
  publishOnRun: boolean;
  endAtOffsetDays: number | null;
}

function tsToIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate().toISOString();
    } catch {
      return new Date().toISOString();
    }
  }
  return new Date().toISOString();
}

function asStrategy(value: unknown): AiVerificationPlanStrategy {
  return value === 'round_robin' || value === 'all_users' ? value : 'random_per_user';
}

function asConstraints(value: unknown): AiVerificationGenerationConstraints {
  const v = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const out: AiVerificationGenerationConstraints = { excludeOwnPosts: v.excludeOwnPosts === true };
  if (typeof v.maxPerUser === 'number' && v.maxPerUser > 0) out.maxPerUser = v.maxPerUser;
  if (typeof v.minOwnPosts === 'number' && v.minOwnPosts > 0) out.minOwnPosts = v.minOwnPosts;
  if (v.preferOwnPosts === true) out.preferOwnPosts = true;
  if (v.sameSessionOnly === true) out.sameSessionOnly = true;
  return out;
}

function asRuns(value: unknown): Record<string, AiVerificationAssignmentPlanRun> {
  const out: Record<string, AiVerificationAssignmentPlanRun> = {};
  if (!value || typeof value !== 'object') return out;
  for (const [k, raw] of Object.entries(value as Record<string, unknown>)) {
    const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    if (typeof r.setId !== 'string') continue;
    out[k] = {
      setId: r.setId,
      lastRunAt: tsToIso(r.lastRunAt),
      lastRunBy: typeof r.lastRunBy === 'string' ? r.lastRunBy : '',
      assignmentCount: typeof r.assignmentCount === 'number' ? r.assignmentCount : 0,
      postCount: typeof r.postCount === 'number' ? r.postCount : 0,
      userCount: typeof r.userCount === 'number' ? r.userCount : 0,
      usersWithoutAssignment:
        typeof r.usersWithoutAssignment === 'number' ? r.usersWithoutAssignment : 0,
      runCount: typeof r.runCount === 'number' ? r.runCount : 0,
    };
  }
  return out;
}

/** Firestore doc → AiVerificationAssignmentPlan (defensive narrow)。 */
export function mapAssignmentPlan(snap: {
  id: string;
  data: () => DocumentData;
}): AiVerificationAssignmentPlan {
  const data = snap.data();
  const cf = (data.candidateFilters ?? {}) as Record<string, unknown>;
  const lock = data.runLock as { startedAt?: unknown; actorUid?: unknown } | null | undefined;
  return {
    id: snap.id,
    displayName: typeof data.displayName === 'string' ? data.displayName : '',
    description: typeof data.description === 'string' ? data.description : '',
    templateId: typeof data.templateId === 'string' ? data.templateId : '',
    scopeMode: data.scopeMode === 'perSession' ? 'perSession' : 'district',
    targetSessionIds: Array.isArray(data.targetSessionIds)
      ? data.targetSessionIds.filter((x): x is string => typeof x === 'string')
      : [],
    strategy: asStrategy(data.strategy),
    constraints: asConstraints(data.constraints),
    candidateFilters: {
      aiAnalyzedOnly: cf.aiAnalyzedOnly !== false,
      includeUnlinkedPosts: cf.includeUnlinkedPosts === true,
    },
    publishOnRun: data.publishOnRun !== false,
    endAtOffsetDays:
      typeof data.endAtOffsetDays === 'number' && data.endAtOffsetDays >= 0
        ? data.endAtOffsetDays
        : null,
    status: data.status === 'archived' ? 'archived' : 'active',
    runs: asRuns(data.runs),
    runLock:
      lock && typeof lock.startedAt === 'string'
        ? {
            startedAt: lock.startedAt,
            actorUid: typeof lock.actorUid === 'string' ? lock.actorUid : '',
          }
        : null,
    createdAt: tsToIso(data.createdAt),
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    updatedAt: tsToIso(data.updatedAt),
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : '',
  };
}

/** 地区の予約を購読 (active を先頭、更新日時の新しい順)。 */
export function useAssignmentPlans(districtId: string | null): AssignmentPlansState {
  const [state, setState] = useState<AssignmentPlansState>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    if (!districtId) {
      setState({ status: 'error', items: [], error: new Error('districtId is required') });
      return;
    }
    const q = collection(db, 'districts', districtId, 'aiVerificationAssignmentPlans');
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map(mapAssignmentPlan);
        items.sort((a, b) => {
          if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
          return b.updatedAt.localeCompare(a.updatedAt);
        });
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return () => unsub();
  }, [districtId]);

  return state;
}

export async function loadAssignmentPlan(
  districtId: string,
  planId: string,
): Promise<AiVerificationAssignmentPlan | null> {
  const ref = doc(db, 'districts', districtId, 'aiVerificationAssignmentPlans', planId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return mapAssignmentPlan({ id: snap.id, data: () => snap.data() as DocumentData });
}

/** undefined キーを落とし、constraints の入れ子も同様に掃除する。 */
function cleanInput(input: Partial<AssignmentPlanInput>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined) continue;
    if (k === 'constraints' && v && typeof v === 'object') {
      const c: Record<string, unknown> = {};
      for (const [ck, cv] of Object.entries(v as Record<string, unknown>)) {
        if (cv !== undefined) c[ck] = cv;
      }
      out[k] = c;
    } else {
      out[k] = v;
    }
  }
  return out;
}

export async function createAssignmentPlan(
  districtId: string,
  input: AssignmentPlanInput,
  actorUid: string,
): Promise<string> {
  const ref = collection(db, 'districts', districtId, 'aiVerificationAssignmentPlans');
  const created = await addDoc(ref, {
    schemaVersion: 1,
    ...cleanInput(input),
    status: 'active',
    runs: {},
    runLock: null,
    createdAt: serverTimestamp(),
    createdBy: actorUid,
    updatedAt: serverTimestamp(),
    updatedBy: actorUid,
  });
  return created.id;
}

export async function saveAssignmentPlanPatch(
  districtId: string,
  planId: string,
  patch: Partial<AssignmentPlanInput> & { status?: AiVerificationAssignmentPlan['status'] },
  actorUid: string,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'aiVerificationAssignmentPlans', planId);
  const { status, ...rest } = patch;
  await setDoc(
    ref,
    {
      ...cleanInput(rest),
      ...(status !== undefined ? { status } : {}),
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    },
    { merge: true },
  );
}

/** 予約を削除する (rules で admin に許可。生成済みの配分セットは残る)。 */
export async function deleteAssignmentPlan(districtId: string, planId: string): Promise<void> {
  await deleteDoc(doc(db, 'districts', districtId, 'aiVerificationAssignmentPlans', planId));
}
